import React, { useRef, useState, useCallback } from 'react';
import ReactCrop, { type Crop, centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Upload, Link2, X, Check, RotateCcw } from 'lucide-react';
import { Button } from './Button';
import { Avatar } from './Avatar';
import { toast } from 'sonner';
import { apiPost, apiDelete, apiUpload } from '../../api/client';

interface AvatarUploadProps {
  currentAvatar?: string | null;
  name?: string;
  onSuccess: (avatarUrl: string | null) => void;
  userId?: string;
}

function centerAspectCrop(w: number, h: number): Crop {
  return centerCrop(
    makeAspectCrop({ unit: '%', width: 80 }, 1, w, h),
    w, h,
  );
}

async function getCroppedBlob(
  image: HTMLImageElement,
  crop: Crop,
  fileName: string,
): Promise<Blob> {
  const canvas = document.createElement('canvas');

  const displayW = image.width;
  const displayH = image.height;

  const pixelX = crop.unit === '%' ? (crop.x / 100) * displayW : crop.x;
  const pixelY = crop.unit === '%' ? (crop.y / 100) * displayH : crop.y;
  const pixelW = crop.unit === '%' ? (crop.width / 100) * displayW : crop.width;
  const pixelH = crop.unit === '%' ? (crop.height / 100) * displayH : crop.height;

  const x = Math.max(0, pixelX);
  const y = Math.max(0, pixelY);
  const cropW = Math.max(1, pixelW);
  const cropH = Math.max(1, pixelH);

  const scaleX = displayW > 0 ? image.naturalWidth / displayW : 1;
  const scaleY = displayH > 0 ? image.naturalHeight / displayH : 1;

  const srcX = x * scaleX;
  const srcY = y * scaleY;
  const srcW = cropW * scaleX;
  const srcH = cropH * scaleY;

  const outputSize = Math.min(srcW, srcH, 400);
  canvas.width = outputSize;
  canvas.height = outputSize;

  const ctx = canvas.getContext('2d')!;
  ctx.drawImage(
    image,
    srcX, srcY, srcW, srcH,
    0, 0, outputSize, outputSize,
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      blob => blob ? resolve(blob) : reject(new Error('Canvas is empty')),
      'image/jpeg',
      0.87,
    );
  });
}

export function AvatarUpload({ currentAvatar, name, onSuccess }: AvatarUploadProps) {
  const [mode, setMode] = useState<'idle' | 'crop' | 'url'>('idle');
  const [srcImg, setSrcImg] = useState<string | null>(null);
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<Crop>();
  const [urlInput, setUrlInput] = useState('');
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const onFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      setSrcImg(ev.target?.result as string);
      setMode('crop');
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  }, []);

  const onImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    // Usa display width/height per calcolare le % del crop. ReactCrop lavora su dimensioni CSS,
    // non su naturalWidth. centerAspectCrop riceve le dimensioni visualizzate.
    const { width: w, height: h } = e.currentTarget;
    if (w > 0 && h > 0) setCrop(centerAspectCrop(w, h));
  }, []);

  const onCropChange = useCallback((c: Crop) => {
    // Aggiorna sia crop (UI) che completedCrop (upload) in tempo reale durante drag
    setCrop(c);
    setCompletedCrop(c);
  }, []);

  const uploadCropped = async () => {
    if (!imgRef.current) {
      toast.error('Seleziona un\'area di ritaglio');
      return;
    }
    const activeCrop = completedCrop ?? crop;
    if (!activeCrop) {
      toast.error('Seleziona un\'area di ritaglio');
      return;
    }
    setUploading(true);
    try {
      const blob = await getCroppedBlob(imgRef.current, activeCrop, 'avatar.jpg');
      const fd = new FormData();
      fd.append('file', blob, 'avatar.jpg');

      const result = await apiUpload<{ avatarUrl: string }>('/auth/avatar', fd);
      onSuccess(result.avatarUrl ?? null);
      setMode('idle');
      setSrcImg(null);
      toast.success('Avatar aggiornato');
    } catch {
      toast.error('Errore nel caricamento');
    }
    setUploading(false);
  };

  const saveUrl = async () => {
    if (!urlInput.startsWith('http')) {
      toast.error('Inserisci un URL valido');
      return;
    }
    setUploading(true);
    try {
      await apiPost('/auth/profile', { avatarUrl: urlInput });
      onSuccess(urlInput);
      setMode('idle');
      setUrlInput('');
      toast.success('Avatar aggiornato');
    } catch {
      toast.error('Errore nel salvataggio');
    }
    setUploading(false);
  };

  const removeAvatar = async () => {
    setRemoving(true);
    try {
      await apiDelete('/auth/avatar');
      onSuccess(null);
      toast.success('Avatar rimosso');
    } catch {
      toast.error('Errore nella rimozione');
    }
    setRemoving(false);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {/* Preview + azioni */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '1.25rem' }}>
        <Avatar src={currentAvatar} name={name} size={72} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <Button size="sm" variant="secondary" icon={<Upload size={14} />}
              onClick={() => fileRef.current?.click()}>
              Carica immagine
            </Button>
            <Button size="sm" variant="secondary" icon={<Link2 size={14} />}
              onClick={() => setMode(mode === 'url' ? 'idle' : 'url')}>
              URL esterno
            </Button>
            {currentAvatar && (
              <Button size="sm" variant="ghost" icon={<X size={14} />}
                onClick={removeAvatar} loading={removing}>
                Rimuovi
              </Button>
            )}
          </div>
          <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)' }}>
            JPEG, PNG o WebP · max 5MB · verrà ritagliato quadrato 400×400
          </p>
        </div>
      </div>

      <input ref={fileRef} type="file" accept="image/*" onChange={onFileSelect} style={{ display: 'none' }} />

      {/* URL input */}
      {mode === 'url' && (
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <input
            value={urlInput}
            onChange={e => setUrlInput(e.target.value)}
            placeholder="https://esempio.com/avatar.jpg"
            style={{
              flex: 1, padding: '0.5rem 0.75rem',
              background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)',
              borderRadius: 'var(--radius-card)', color: 'var(--color-text)',
              fontSize: 'var(--text-sm)',
            }}
          />
          <Button size="sm" icon={<Check size={14} />} onClick={saveUrl} loading={uploading}>
            Salva
          </Button>
          <Button size="sm" variant="ghost" icon={<X size={14} />} onClick={() => setMode('idle')} />
        </div>
      )}

      {/* Crop UI */}
      {mode === 'crop' && srcImg && (
        <div style={{
          border: '1px solid rgba(255,255,255,0.10)', borderRadius: 'var(--radius-card)',
          padding: '1rem', background: 'var(--glass-bg-card)',
          display: 'flex', flexDirection: 'column', gap: '1rem',
        }}>
          <div style={{
            borderRadius: 'var(--radius-card)',
            touchAction: 'none',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
          }}>
            <ReactCrop
              crop={crop}
              onChange={onCropChange}
              onComplete={() => { if (crop) setCompletedCrop(crop); }}
              aspect={1}
              circularCrop
            >
              <img
                ref={imgRef}
                src={srcImg}
                alt="Crop"
                onLoad={onImageLoad}
                style={{
                  width: 'auto',
                  height: 'auto',
                  maxWidth: '100%',
                  maxHeight: '300px',
                  display: 'block',
                  userSelect: 'none',
                }}
              />
            </ReactCrop>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
            <Button size="sm" variant="ghost" icon={<RotateCcw size={14} />}
              onClick={() => { setMode('idle'); setSrcImg(null); }}>
              Annulla
            </Button>
            <Button size="sm" icon={<Check size={14} />} onClick={uploadCropped} loading={uploading}>
              Salva avatar
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
